import { useEffect, useState, useCallback } from 'react';
import { getTodayString } from '../utils/dateUtils';

/**
 * useToday — returns today's LOCAL date string (YYYY-MM-DD) and keeps it fresh.
 *
 * The value refreshes when:
 *   - the app is resumed / tab becomes visible again (visibilitychange, focus)
 *   - the device crosses LOCAL midnight while the app is left open (scheduled timer)
 *
 * This prevents Home from being stuck on "yesterday" after the app has been open
 * across midnight or backgrounded overnight.
 */
export function useToday() {
  const [today, setToday] = useState(getTodayString());

  const refresh = useCallback(() => {
    const now = getTodayString();
    setToday((prev) => (prev === now ? prev : now));
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);

    // Schedule a refresh at the next LOCAL midnight, then re-arm each day.
    let timer;
    const arm = () => {
      const now = new Date();
      const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 1, 0);
      const ms = Math.max(1000, next.getTime() - now.getTime());
      timer = setTimeout(() => {
        refresh();
        arm();
      }, ms);
    };
    arm();

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
      if (timer) clearTimeout(timer);
    };
  }, [refresh]);

  return today;
}

export default useToday;
